import breakpoint from '../util/breakpoint';
import * as mapStyle from '../util/GoogleMapStyle.json';

/* global google */

export default class CompaniesMap {
  /**
   * Companies map
   *
   * @param {string} apiKey  The Google maps api key
   * @param {HTMLElement} mapButtonElt  The button to open the map
   * @param {CompaniesList} companiesList  The CompaniesList object
   * @param {CompaniesSearch} companiesSearch  The Search object
   * @param {HTMLElement} detailsElt  The company details element
   */
  constructor(apiKey, mapButtonElt, companiesList, companiesSearch, detailsElt) {
    /**
     * @property {string} apiKey  The Google maps api key
     */
    this.apiKey = apiKey;

    /**
     * @property {HTMLElement} mapButtonElt  The button to open the map
     */
    this.mapButtonElt = mapButtonElt;

    /**
     * @property {CompaniesList} companiesList  The CompaniesList object
     */
    this.companiesList = companiesList;

    /**
     * @property {Search} search  The CompaniesSearch object
     */
    this.companiesSearch = companiesSearch;

    /**
     * @property {HTMLElement} detailsElt  The company details element
     */
    this.detailsElt = detailsElt;

    /**
     * @property {HTMLElement} mapElt  The map wrapper element
     */
    this.mapElt = null;

    /**
     * @property {HTMLElement} closeButtonElt  The map close button element
     */
    this.closeButtonElt = null;

    /**
     * @property {HTMLElement} mapCanvasElt  The map canvas element
     */
    this.mapCanvasElt = null;

    /**
     * @property {google.maps.Map} map  The Google map object
     */
    this.map = null;

    /**
     * @property {google.maps.Marker[]} markers  The Google map markers
     */
    this.markers = [];

    /**
     * @property {number} initialZoom  The initial zoom level of the map
     */
    this.initialZoom = 12;

    /**
     * @property {number} maxZoom  The maximal zoom level to apply to the map
     */
    this.maxZoom = 15;

    /**
     * @property {Object} markerIcon  The marker icon
     * @see https://developers.google.com/maps/documentation/javascript/symbols?hl=fr#properties
     */
    this.markerIcon = {};

    /**
     * @property {bool} isOpen  Whether the map is open
     */
    this.isOpen = false;

    /** Bind the events */
    this.bindEvents();
  }

  /**
   * Bind the events
   */
  bindEvents() {
    this.onMouseOverCompanyHandler = this.onMouseOverCompany.bind(this);
    this.onMouseOutCompanyHandler = this.onMouseOutCompany.bind(this);
    this.onMouseOverMarkerHandler = this.onMouseOverMarker.bind(this);
    this.onMouseOutMarkerHandler = this.onMouseOutMarker.bind(this);
    this.onClickMarkerHandler = this.onClickMarker.bind(this);
    this.onCompaniesListChangeHandler = this.onCompaniesListChange.bind(this);
    this.onCompaniesListBeforeChangeHandler = this.onCompaniesListBeforeChange.bind(this);
    this.companiesList.companiesElt.addEventListener('change', this.onCompaniesListChangeHandler);
    this.companiesList.companiesElt.addEventListener('beforechange', this.onCompaniesListBeforeChangeHandler);
    this.detailsElt.addEventListener('close', () => {
      if (this.isOpen) {
        /** Remove the body scrollbar */
        document.querySelector('body').style.overflow = 'hidden';
      }
    });
    this.mapButtonElt.addEventListener('click', e => {
      e.preventDefault();
      this.loadScriptAndOpenMap();
    });
  }

  /**
   * Load Google Maps script and open the map
   */
  loadScriptAndOpenMap() {
    if (document.getElementById('map-script') === null) {
      const script = document.createElement('script');
      script.id = 'map-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&callback=onMapsInit`;
      document.getElementsByTagName('body')[0].appendChild(script);
      window.onMapsInit = () => this.initialize();
    } else {
      this.openMap();
    }
  }

  /**
   * Initialize
   */
  initialize() {
    /** Wrap the search & the companies with the map */
    this.mapElt = document.createElement('div');
    this.mapElt.className = 'search-map is-hidden';
    this.companiesSearch.searchElt.parentNode.insertBefore(this.mapElt, this.companiesSearch.searchElt);
    this.mapElt.appendChild(this.companiesSearch.searchElt);
    this.mapElt.appendChild(this.companiesList.companiesElt);

    /** Append the logo to the search form */
    const logo = document.querySelector('#head .head-logo').cloneNode(true);
    this.companiesSearch.searchFormElt.insertBefore(logo, this.companiesSearch.searchFormElt.firstChild);

    /** Append the close button */
    const closeIcon = document.createElement('span');
    closeIcon.classList = 'sr-hidden icon icon-arrow-left';
    closeIcon.setAttribute('aria-hidden', 'true');
    const closeText = document.createElement('span');
    closeText.classList = 'sr-only';
    closeText.innerHTML = this.mapButtonElt.getAttribute('data-txt-close');
    this.closeButtonElt = document.createElement('button');
    this.closeButtonElt.classList = 'button button-round search-map-close';
    this.closeButtonElt.appendChild(closeIcon);
    this.closeButtonElt.appendChild(closeText);
    this.closeButtonElt.addEventListener('click', this.closeMap.bind(this));
    this.mapElt.appendChild(this.closeButtonElt);

    /** Append the map canvas */
    this.mapCanvasElt = document.createElement('div');
    this.mapCanvasElt.classList = 'search-map-canvas';
    this.mapElt.appendChild(this.mapCanvasElt);

    /** Open the map */
    this.openMap();
  }

  /**
   * Initialize the Google map
   */
  initMap() {
    /** Create the map */
    this.map = new google.maps.Map(this.mapCanvasElt, {
      center: new google.maps.LatLng(
        parseFloat(this.mapButtonElt.getAttribute('data-maps-lat')),
        parseFloat(this.mapButtonElt.getAttribute('data-maps-lng'))
      ),
      zoom: this.initialZoom,
    });

    /** Style the map */
    const styledMapType = new google.maps.StyledMapType(mapStyle, { name: 'Made in Luxembourg' });
    this.map.mapTypes.set('styled_map', styledMapType);
    this.map.setMapTypeId('styled_map');

    /** Set the map markers */
    this.setMarkers();
  }

  /**
   * Set the map markers
   */
  setMarkers() {
    /**
     * Create the marker icon
     * @see https://developers.google.com/maps/documentation/javascript/symbols?hl=fr#properties
     */
    this.markerIcon = {
      path:
        'M7.2,20.6c-6-8.6-7.1-9.5-7.1-12.7C0.1,3.5,3.7,0,8,0c4.3,0,7.9,3.5,7.9,7.9 c0,3.2-1.1,4.1-7.1,12.7c-0.3,0.4-0.9,0.6-1.4,0.2C7.3,20.8,7.3,20.7,7.2,20.6L7.2,20.6z M8,11.2c1.8,0,3.3-1.5,3.3-3.3 S9.8,4.6,8,4.6c-1.8,0-3.3,1.5-3.3,3.3C4.7,9.7,6.2,11.2,8,11.2C8,11.2,8,11.2,8,11.2L8,11.2z',
      fillColor: '#000953',
      fillOpacity: 1.0,
      strokeWeight: 0,
      rotation: 0,
      scale: 1,
      anchor: new google.maps.Point(8, 20),
    };

    /** Browse the companies displayed in CompaniesList */
    for (const company of this.companiesList.companiesArticlesElts) {
      const companyLink = company.querySelector('a');
      let lat = '';
      let lng = '';
      let id = '';
      if (companyLink !== null) {
        lat = companyLink.getAttribute('data-lat');
        lng = companyLink.getAttribute('data-lng');
        id = companyLink.getAttribute('data-id');

        /** Only create a marker if the company has GPS coordinates */
        if (lat.length > 0 && lng.length > 0) {
          /** Create the marker */
          const marker = new google.maps.Marker({
            position: new google.maps.LatLng(lat, lng),
            map: this.map,
            icon: this.markerIcon,
            title: companyLink.querySelector('.company-name').innerHTML,
            id,
          });
          this.markers.push(marker);

          /** Bind the mouse events */
          companyLink.addEventListener('mouseover', this.onMouseOverCompanyHandler);
          companyLink.addEventListener('mouseout', this.onMouseOutCompanyHandler);
          marker.addListener('mouseover', () => this.onMouseOverMarkerHandler(id));
          marker.addListener('mouseout', () => this.onMouseOutMarkerHandler(id));
          marker.addListener('click', () => this.onClickMarkerHandler(id));
        }
      }
    }

    if (this.markers.length > 0) {
      /** Scale and center the map to show all the markers */
      const bounds = new google.maps.LatLngBounds();
      for (const marker of this.markers) {
        bounds.extend(marker.position);
      }
      this.map.fitBounds(bounds);

      /** Prevent the map zoom from exceeding maximum allowed zoom */
      if (this.map.getZoom() > this.maxZoom) {
        this.map.setZoom(this.maxZoom);
      }
    } else {
      /** Restore the original map position & zoom */
      this.map.setCenter(
        new google.maps.LatLng(
          parseFloat(this.mapButtonElt.getAttribute('data-maps-lat')),
          parseFloat(this.mapButtonElt.getAttribute('data-maps-lng'))
        )
      );
      this.map.setZoom(this.initialZoom);
    }

    /** Translate the map to avoid the UI to overlap the markers */
    if (window.matchMedia(breakpoint.breakpointUp('lg')).matches) {
      this.map.panBy(this.companiesList.companiesElt.offsetWidth / 2, 0);
    } else {
      this.map.panBy(0, -this.companiesSearch.searchFormElt.offsetHeight / 2);
    }
  }

  /**
   * Unset the map markers
   */
  unsetMarkers() {
    for (const marker of this.markers) {
      const companyLink = this.companiesList.listElt.querySelector(`[data-id='${marker.id}']`);
      companyLink.removeEventListener('mouseover', this.onMouseOverCompanyHandler);
      companyLink.removeEventListener('mouseout', this.onMouseOutCompanyHandler);
      google.maps.event.clearListeners(marker, 'mouseover');
      google.maps.event.clearListeners(marker, 'mouseout');
      google.maps.event.clearListeners(marker, 'click');
      marker.setMap(null);
      this.markers = [];
    }
  }

  /**
   * CompaniesList beforechange handler
   */
  onCompaniesListBeforeChange() {
    if (this.isOpen) {
      this.unsetMarkers();
    }
  }

  /**
   * CompaniesList change handler
   */
  onCompaniesListChange() {
    if (this.isOpen) {
      this.setMarkers();
    }
  }

  /**
   * CompaniesList item mouseover handler
   *
   * Highlight the matching google.maps.Marker icon
   *
   * @param {MouseEvent} e
   */
  onMouseOverCompany(e) {
    const id = e.currentTarget.getAttribute('data-id');
    const marker = this.markers.find(marker => marker.id === id);
    this.markerIcon.scale = 2;
    this.markerIcon.fillColor = '#6a48b7';
    marker.setIcon(this.markerIcon);
  }

  /**
   * CompaniesList item mouseout handler
   *
   * Remove the highlight of the matching google.maps.Marker icon
   *
   * @param {MouseEvent} e
   */
  onMouseOutCompany(e) {
    const id = e.currentTarget.getAttribute('data-id');
    const marker = this.markers.find(marker => marker.id === id);
    this.markerIcon.scale = 1;
    this.markerIcon.fillColor = '#000953';
    marker.setIcon(this.markerIcon);
  }

  /**
   * google.maps.Marker mouseover handler
   *
   * Highlight the matching CompaniesList item and srroll it into view
   *
   * @param {String} id  The company id
   */
  onMouseOverMarker(id) {
    this.companiesList.listElt.querySelector(`[data-id='${id}']`).classList.add('has-focus');
    this.companiesList.listElt.querySelector(`[data-id='${id}']`).scrollIntoView();
  }

  /**
   * google.maps.Marker mouseout handler
   *
   * Remove the highlight of the matching CompaniesList item
   *
   * @param {String} id  The company id
   */
  onMouseOutMarker(id) {
    this.companiesList.listElt.querySelector(`[data-id='${id}']`).classList.remove('has-focus');
  }

  /**
   * Map marker click handler
   *
   * Fire a click to the matching CompaniesList link to open a CompanyDetails window
   *
   * @param {String} id  The company id
   */
  onClickMarker(id) {
    this.companiesList.listElt.querySelector(`[data-id='${id}']`).click();
  }

  /**
   * Open the map and initialize
   */
  openMap() {
    this.mapElt.classList.remove('is-hidden');
    this.mapElt.classList.add('is-open');
    this.isOpen = true;
    this.initMap();

    /** Tell to CompaniesSearch that the map is open */
    this.companiesSearch.isCompanyMapOpen = true;

    /** Remove the body scrollbar */
    document.querySelector('body').style.overflow = 'hidden';
  }

  /**
   * Close the map and unset the markers
   */
  closeMap() {
    this.unsetMarkers();
    this.mapElt.classList.remove('is-open');
    this.mapElt.classList.add('is-hidden');
    this.isOpen = false;

    /** Tell to CompaniesSearch that the map is closed */
    this.companiesSearch.isCompanyMapOpen = false;

    /** Restore the body scrollbar */
    document.querySelector('body').style.overflow = 'visible';
  }
}



// WEBPACK FOOTER //
// ./scripts/components/CompaniesMap.js